import axios, { AxiosError, AxiosResponse } from 'axios';

interface ErrorResponse {
    msg?: string;
    message?: string;
    [key: string]: unknown;
}

interface GetIp {
    get?: boolean;
    fieldValue?: string | undefined;
}

interface ApiHitParams {
    method?: 'get' | 'post' | 'put' | 'delete';
    data?: Record<string, unknown> | null;
    token?: string | null;
    url: string;
    headers?: Record<string, string>;
    getIp?: GetIp;
    signal?: AbortSignal;
}

/**
 * @heading1: API Service
 */
class ApiService {
    // Explicitly declare the baseURL property
    baseURL: string;
    constructor () {
        /**
         * @heading3: Constructor initializes API service.
         * @block_quotes: Sets the base URL for API requests using environment variables.
         * @code:
         * const apiService = new ApiService();
         * console.log(apiService.baseURL); // Outputs base URL
         * @separator:
         */
        this.baseURL = process.env.REACT_APP_API_BASE_URL as string;
    }
    private static cachedIp: string | null = null;

    /**
     * @heading3: API Request Handler
     * @danger: Parameters
     * @ol:
     * url : {string} - The endpoint URL (relative to baseURL).
     * token=null : {string | null} - Optional bearer token for authentication.
     * method='get' : {'get' | 'post' | 'put' | 'delete'} - HTTP method.
     * data=null : {Record<string, unknown> | null} - Request body for POST/PUT methods.
     * headers={} : {Record<string, string>} - Additional headers for the request.
     * getIp : {GetIp} - Optional IP injection details.
     * @danger: Return
     * @block_quotes: A promise resolving to the response data. {Promise<T>}
     * @code:
     * apiService.apiHit({
     *     url: 'endpoint',
     *     method: 'post',
     *     data: { key: 'value' }
     * }).then(response => console.log(response));
     */
    async apiHit<T = Record<string, unknown>> ({
        method = 'get',
        data = null,
        token = null,
        url = '',
        headers = {},
        getIp = {
            get: false,
            fieldValue: 'user_ip'
        },
        signal
    }: ApiHitParams): Promise<T> {
        const localController = signal ? null : new AbortController();
        const usedSignal = signal || localController?.signal;
        try {
            let payload = data;
            const { fieldValue, get } = getIp;
            if (get && fieldValue) {
                /**
                 * @heading4: IP Injection.
                 * @block_quotes: Adds the user's public IP to the payload if required.
                 */
                const myIp = await ApiService.getIpAddress();
                payload = { ...payload, [fieldValue]: myIp };
            }
            const localHeaders: Record<string, string | null> = {
                'Authorization': token ? `Bearer ${token}` : null,
                'Content-Type': 'application/json',
                ...headers
            };
            if (process.env.REACT_APP_MODE === 'dev') {
                // I want to add ngrok-skip-browser-warning true when it is dev mode
                localHeaders['ngrok-skip-browser-warning'] = 'true';
            }
            const response: AxiosResponse<T> = await axios({
                method,
                url: this.baseURL + url,
                data: payload,
                headers: localHeaders,
                signal: usedSignal,
                timeout: 300000
            });
            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                /**
                * @heading4: Error Handling.
                * @block_quotes: Handles Axios-specific errors using a dedicated error handler.
                * @separator:
                */
                return ApiService.handleError(error);
            }
            throw new Error('An unexpected error occurred');
        }
    }

    /**
      * @heading3: Get IP Address
      * @danger: Return
      * @block_quotes: A promise resolving to the user's public IP address. {Promise<string>}
      * @code:
      * ApiService.getIpAddress().then(ip => console.log(ip));
      * @separator:
      */
    static getIpAddress () {
        // if (this.cachedIp) {
        //     // Return the cached IP if available
        //     return Promise.resolve(this.cachedIp);
        // }
        // try {
        //     const response: AxiosResponse<{ ip: string }> = await axios.get('https://api.ipify.org?format=json');
        //     this.cachedIp = response.data.ip;
        //     return this.cachedIp;
        // } catch (error: unknown) {
        //     if (axios.isAxiosError(error)) {
        //         await ApiService.handleError(error);
        //     }
        //     throw new Error('An unexpected error occurred');
        // }
        return '0.0.0.0';
    }

    /**
     * @heading3: API Error Handler
     * @danger: Parameter
     * @ol:
     * error : {AxiosError} - The error object.
     * @danger: Return
     * @block_quotes: A rejected promise with a user-friendly error message and Customizes error messages based on HTTP status codes. {Promise<never>}
     * @code:
     * try {
     *     await apiService.apiHit({ url: 'endpoint' });
     * } catch (error) {
     *     console.error(error.message);
     * }
      * @separator:
     */
    static handleError (error: AxiosError): Promise<never> {
        let errorMessage = 'An unknown error occurred. Please try again later.';
        // let errorDetails: AxiosError | unknown = error;
        if (axios.isAxiosError(error)) {
            const { response } = error;
            // errorDetails = error;
            if (response) {
                const { status, data } = response as { status: number; data: ErrorResponse };
                switch (status) {
                    case 400:
                        errorMessage = data?.msg ? data.msg : 'Bad Request. Please check your input.';
                        break;
                    case 401:
                        errorMessage = data?.msg ? data.msg : 'Your session has expired.';
                        break;
                    case 403:
                        errorMessage = data?.msg ? data.msg : 'Forbidden. You don\'t have permission.';
                        break;
                    case 404:
                        errorMessage = data?.msg ? data.msg : 'Resource not found.';
                        break;
                    case 422:
                        errorMessage = data?.msg ? data.msg : 'Unprocessable entity.';
                        break;
                    case 500:
                        errorMessage = data?.msg ? data.msg : 'Something went wrong. Please try again later.';
                        break;
                    default:
                        errorMessage =
                            data.message || `Error: ${response.statusText} (${status})`;
                }
            } else if (error.message) {
                errorMessage = ApiService.getNetworkErrorMessage(error.message);
            }
        }

        return Promise.reject(new Error(errorMessage));
    }

    /**
     * @heading3: Network Error Handler
     * @danger: parameter
     * @ol:
     * message : {string} - The error message.
     * @danger: Return
     * @block_quotes:  A user-friendly error message. Translates technical error messages into user-friendly text. {string}
     * @code:
     * const errorMsg = ApiService.getNetworkErrorMessage('Network Error');
     * console.log(errorMsg); // Outputs user-friendly error text
      * @separator:
     */
    static getNetworkErrorMessage (message: string): string {
        if (message.includes('Network Error')) {
            return 'Network error: Unable to connect. This could be a CORS issue or network issue.';
        } else if (message.includes('timeout')) {
            return 'The request timed out. Please try again later.';
        }
        return message;
    }
}

// Export the ApiService instance
export const apiService = new ApiService();
export default apiService;